import { useEffect, useRef, useState } from "react";

/**
 * Lazy-friendly hero video.
 *
 * - Renders a poster image instantly so the box never looks empty.
 * - Mounts a single <video> element (no IntersectionObserver gating that has
 *   bitten us before — autoplay policies hate dynamically inserted videos).
 * - Uses <source media="..."> so the browser natively picks the mobile
 *   variant (≤ 768 px) over the desktop one. Browser only fetches the chosen
 *   source.
 * - Respects `prefers-reduced-motion`: shows poster only.
 */
export default function LazyHeroVideo({
  poster,
  sources, // [{ src, media? }] – first matching <source> wins; last entry is the desktop default (no media)
  className = "",
  children,
  ...rest
}) {
  const [reducedMotion, setReducedMotion] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Some mobile browsers refuse autoplay until we explicitly call .play().
  useEffect(() => {
    const v = videoRef.current;
    if (!v || reducedMotion) return;
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    if (v.readyState >= 2) tryPlay();
    else v.addEventListener("loadeddata", tryPlay, { once: true });
    return () => v.removeEventListener("loadeddata", tryPlay);
  }, [reducedMotion]);

  return (
    <div className={`relative ${className}`}>
      {reducedMotion ? (
        <img
          src={poster}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <video
          ref={videoRef}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          {...rest}
        >
          {sources.map((s, i) => (
            <source
              key={s.src}
              src={s.src}
              type="video/mp4"
              {...(s.media && i < sources.length - 1 ? { media: s.media } : {})}
            />
          ))}
        </video>
      )}
      {children}
    </div>
  );
}
